import React, { createContext, ReactNode, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { useAudioPlayer } from "@/hooks/useAudioPlayer";
import { useVoiceRecording } from "@/hooks/useVoiceRecording";
import { createVoiceResponse } from "@/services/voiceService";

interface VoiceContextType {
  isListening: boolean;
  isProcessing: boolean;
  isPlaying: boolean;
  startListening: (persona: string) => void;
  stopListening: () => void;
  stopPlayback: () => void;
  silenceDetectionEnabled: boolean;
  toggleSilenceDetection: () => void;
  selectedPersonality: string;
  setSelectedPersonality: (personality: string) => void;
}

const VoiceContext = createContext<VoiceContextType | undefined>(undefined);

export const useVoice = (): VoiceContextType => {
  const context = useContext(VoiceContext);
  if (!context) {
    throw new Error("useVoice must be used within a VoiceProvider");
  }
  return context;
};

export const VoiceProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedPersona, setSelectedPersona] = useState<string | null>(null);
  const [silenceDetectionEnabled, setSilenceDetectionEnabled] = useState(true);
  const [selectedPersonality, setSelectedPersonality] = useState<string>('shimmer');

  // Audio player hook
  const { play, stop: stopPlaying } = useAudioPlayer();
  
  // Voice recording hook with debug disabled to reduce console noise
  const {
    isListening,
    audioBlob,
    startListening: startVoiceRecording,
    stopListening: stopVoiceRecording,
  } = useVoiceRecording({
    silenceDetectionEnabled,
    onRecordingComplete: () => setIsProcessing(true),
    debug: false // Disable debug to reduce console noise
  });

  const startListening = useCallback(async (persona: string) => {
    console.log(`Starting listening with persona: ${persona}`);
    setSelectedPersona(persona);
    await startVoiceRecording();
  }, [startVoiceRecording]);

  const toggleSilenceDetection = useCallback(() => {
    setSilenceDetectionEnabled(prev => !prev);
  }, []);

  // Process audio when recording stops
  useEffect(() => {
    if (audioBlob && !isListening && isProcessing && selectedPersona) {
      processAudioResponse();
    }
    
    async function processAudioResponse() {
      try {
        console.log("Processing audio response...");
        const response = await createVoiceResponse(audioBlob, selectedPersona, selectedPersonality);
        
        if (response) {
          console.log("Playing audio response");
          await play(response);
        } else {
          console.error("No audio response received");
        }
      } catch (error) {
        console.error("Error processing voice response:", error);
      } finally {
        setIsProcessing(false);
      }
    }
  }, [audioBlob, isListening, isProcessing, play, selectedPersona, selectedPersonality]);
  
  // Detect when audio playback stops
  useEffect(() => {
    return () => {
      if (isPlaying) {
        stopPlaying();
      }
    };
  }, []);
  
  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    isListening,
    isProcessing,
    isPlaying,
    startListening,
    stopListening: stopVoiceRecording,
    stopPlayback: stopPlaying,
    silenceDetectionEnabled,
    toggleSilenceDetection,
    selectedPersonality,
    setSelectedPersonality,
  }), [
    isListening,
    isProcessing,
    isPlaying,
    startListening,
    stopVoiceRecording,
    stopPlaying,
    silenceDetectionEnabled,
    toggleSilenceDetection,
    selectedPersonality,
    setSelectedPersonality,
  ]);
  
  return <VoiceContext.Provider value={value}>{children}</VoiceContext.Provider>;
};
